import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
} from 'react';

import useAirport from '@shared/v2/components/Airport/hooks/useAirport/useAirport';

import {
  flyingFromChangeEvent,
  travellingToChangeEvent,
} from '@/v2/utils/dataLayer/searchEvents/events';

interface UseCampaignFiltersProps {
  setOriginCode: Dispatch<SetStateAction<string | null>>;
  originCodes: string[];
  setDestinationName: Dispatch<SetStateAction<string>>;
  tilesScrollTo: RefObject<HTMLDivElement>;
  shouldUseNewEvents: boolean;
}

const useCampaignFilters = ({
  setOriginCode,
  originCodes,
  setDestinationName,
  tilesScrollTo,
  shouldUseNewEvents,
}: UseCampaignFiltersProps) => {
  const { value: airportOriginCode, setValue: setStoredOriginCode } =
    useAirport({
      parameterName: 'originCode',
    });

  useEffect(() => {
    if (airportOriginCode && !originCodes.includes(airportOriginCode)) {
      setOriginCode(null);
      setStoredOriginCode('');
    }
  }, [airportOriginCode, originCodes, setOriginCode, setStoredOriginCode]);

  const scrollIntoView = useCallback(() => {
    tilesScrollTo?.current?.scrollIntoView?.({
      block: 'nearest',
    });
  }, [tilesScrollTo]);

  const handleSetOrigin = useCallback(
    (selectedValue: string | null) => {
      if (selectedValue === null) {
        setOriginCode(null);
        setStoredOriginCode('');
      } else {
        setOriginCode(selectedValue);
        setStoredOriginCode(selectedValue);

        if (shouldUseNewEvents) {
          flyingFromChangeEvent(selectedValue);
        }
      }
      scrollIntoView();
    },
    [scrollIntoView, setOriginCode, setStoredOriginCode, shouldUseNewEvents],
  );

  const handleSetDestination = useCallback(
    (destination: string) => {
      if (shouldUseNewEvents) {
        travellingToChangeEvent(destination);
      }
      setDestinationName(destination);
      scrollIntoView();
    },
    [scrollIntoView, setDestinationName, shouldUseNewEvents],
  );

  return {
    airportOriginCode,
    handleSetOrigin,
    handleSetDestination,
  };
};

export default useCampaignFilters;
